import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import SortIcon from '@mui/icons-material/Sort';
import {
    sortOf,
    type FieldListSort,
    type FieldSortOption,
    type SortDirection
} from './fieldListSort';

const DIRECTIONS: { direction: SortDirection; labelId: string }[] = [
    { direction: 'asc', labelId: 'field.sort.ascending' },
    { direction: 'desc', labelId: 'field.sort.descending' }
];

/**
 * Beside a field list's search: what the list is ordered by, and which way.
 * Picking another column takes its usual direction; picking the current one
 * again turns it round, so most changes are one tap. Tinted while the list
 * stands in anything but its usual order.
 */
export default function FieldSortButton({
    options,
    sort,
    onSort
}: {
    options: readonly FieldSortOption[];
    sort: FieldListSort;
    onSort: (sort: FieldListSort) => void;
}) {
    const intl = useIntl();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const usual = sortOf(options[0]);
    const changed =
        sort.key !== usual.key || sort.direction !== usual.direction;
    const label = intl.formatMessage({ id: 'field.sort.button' });

    function pick(option: FieldSortOption) {
        setAnchor(null);
        onSort(
            option.key === sort.key
                ? {
                      key: sort.key,
                      direction: sort.direction === 'asc' ? 'desc' : 'asc'
                  }
                : sortOf(option)
        );
    }

    function turn(direction: SortDirection) {
        setAnchor(null);
        onSort({ key: sort.key, direction });
    }

    const arrow =
        sort.direction === 'asc' ? (
            <ArrowUpwardIcon fontSize="small" />
        ) : (
            <ArrowDownwardIcon fontSize="small" />
        );

    return (
        <>
            <Tooltip title={label}>
                <IconButton
                    size="large"
                    aria-label={label}
                    aria-haspopup="menu"
                    color={changed ? 'primary' : 'default'}
                    onClick={(event) => setAnchor(event.currentTarget)}
                >
                    <SortIcon />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {options.map((option) => {
                    const active = option.key === sort.key;
                    return (
                        <MenuItem
                            key={option.key}
                            selected={active}
                            onClick={() => pick(option)}
                        >
                            <ListItemIcon>{active && arrow}</ListItemIcon>
                            <ListItemText>
                                <FormattedMessage id={option.labelId} />
                            </ListItemText>
                        </MenuItem>
                    );
                })}
                <Divider />
                {DIRECTIONS.map(({ direction, labelId }) => (
                    <MenuItem key={direction} onClick={() => turn(direction)}>
                        <ListItemIcon>
                            {sort.direction === direction && (
                                <CheckIcon fontSize="small" />
                            )}
                        </ListItemIcon>
                        <ListItemText>
                            <FormattedMessage id={labelId} />
                        </ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
